import React, { useState, useEffect, useRef } from 'react';
import { ref, onValue, update, query, orderByChild, equalTo } from 'firebase/database';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { auth, db } from './firebase';
import { STATUS_OPTIONS, STATUS_COLORS, STATUS_ICONS } from './config';
import s from './styles';
import logo from './assets/logo.png';
import RegisterPage    from './RegisterPage';
import LoginPage       from './LoginPage';
import UserGuide       from './UserGuide';
import InventoryTab    from './InventoryTab';
import EarningsTab     from './EarningsTab';
import SalesTab        from './SalesTab';
import ChatTab         from './ChatTab';
import AnnouncementsTab from './AnnouncementsTab';
import ProfileTab      from './ProfileTab';
import PharmacistsTab  from './PharmacistsTab';
import WorkingHoursTab from './WorkingHoursTab';
import PrescriptionTab from './PrescriptionTab';
import BankAccountTab from './BankAccountTab';

// The rider's optional profile photo (riderPhotos/{uid}, a small JPEG data URI).
// Lets the pharmacist see WHO should be walking in to collect an order.
function useRiderPhoto(riderId) {
  const [photo, setPhoto] = useState(null);
  useEffect(() => {
    if (!riderId) { setPhoto(null); return undefined; }
    const unsub = onValue(ref(db, `riderPhotos/${riderId}`), snap => setPhoto(snap.val() || null));
    return () => unsub();
  }, [riderId]);
  return photo;
}

function RiderPhoto({ riderId, name, size = 56 }) {
  const photo = useRiderPhoto(riderId);
  const base = { width: size, height: size, borderRadius: '50%', flexShrink: 0, objectFit: 'cover' };
  if (photo) return <img src={photo} alt={name || 'Rider'} style={{ ...base, border: '2px solid #BFDBFE' }} />;
  return (
    <div style={{ ...base, backgroundColor: '#DBEAFE', color: '#1D4ED8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: size * 0.4 }}>
      {(name || 'R').trim().charAt(0).toUpperCase()}
    </div>
  );
}

// Pickup handover: while the rider is on the way, show the 4-digit code the
// rider must say back before they can collect this order. (A multi-stop route
// shares one code across its orders.) The code is generated server-side and is
// readable only by this pharmacy and admins — never by the rider's own app.
function PickupCodeBox({ order }) {
  const [code, setCode] = useState(null);
  const [verified, setVerified] = useState(false);
  useEffect(() => {
    const u1 = onValue(ref(db, `orderPickupCodes/${order.id}`), snap => setCode(snap.val()));
    const u2 = onValue(ref(db, `pickupVerified/${order.id}`), snap => setVerified(snap.exists()));
    return () => { u1(); u2(); };
  }, [order.id]);

  const live = code && code.forAcceptedAt === order.acceptedAt ? code : null;
  const refNo = order.orderId?.slice(-6).toUpperCase() || order.id.slice(-6).toUpperCase();
  return (
    <div style={{ marginTop: 10, backgroundColor: '#FFF', border: '2px dashed #3B82F6', borderRadius: 10, padding: '12px 14px', textAlign: 'center' }}>
      <p style={{ fontSize: 12, color: '#1D4ED8', fontWeight: 700, margin: '0 0 4px' }}>
        PICKUP CODE — order #{refNo}
      </p>
      {live ? (
        <p style={{ fontSize: 34, fontWeight: 800, letterSpacing: 10, color: '#1E3A8A', margin: '2px 0 6px', fontFamily: 'monospace' }}>{live.code}</p>
      ) : (
        <p style={{ fontSize: 13, color: '#64748B', margin: '6px 0' }}>Generating the code…</p>
      )}
      <p style={{ fontSize: 12, color: '#64748B', margin: 0, lineHeight: 1.5 }}>
        Hand this order over <strong>only</strong> to the rider shown above — and only after they tell you this code.
        {verified ? ' ✅ The rider has already confirmed this code.' : ''}
      </p>
    </div>
  );
}

// ReceiptModal builds its print window via document.write() with raw
// template interpolation — customer name/address, item names, dosage notes,
// warnings, and counseling notes all originate from user/pharmacy input, so
// without escaping, a crafted value becomes stored XSS that runs in this
// popup (which retains window.opener back to the dashboard tab).
function escapeHtml(str) {
  return String(str ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c]));
}

// ── DARK MODE INIT ────────────────────────────────────────────────────────────
if (localStorage.getItem('darkMode') === '1') {
  document.body.classList.add('dark-mode');
}

// ── RINGTONE ──────────────────────────────────────────────────────────────────
function playOrderRingtone(audioRef) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    audioRef.current = ctx;
    const endTime = ctx.currentTime + 15;
    const playBeep = (startTime) => {
      if (startTime > endTime) return;
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.setValueAtTime(880, startTime);
      osc.frequency.setValueAtTime(660, startTime + 0.15);
      osc.frequency.setValueAtTime(880, startTime + 0.3);
      gain.gain.setValueAtTime(0.4, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.5);
      osc.start(startTime); osc.stop(startTime + 0.5);
      playBeep(startTime + 1.2);
    };
    playBeep(ctx.currentTime);
  } catch (e) { console.log('Audio error:', e); }
}
function stopOrderRingtone(audioRef) {
  try { if (audioRef.current) { audioRef.current.close(); audioRef.current = null; } } catch (e) {}
}

// A distinct two-note chime (not the order ringtone's three-note warble) so
// staff can tell by ear whether a new order or a new prescription just came
// in without looking at the screen.
function playPrescriptionRingtone(audioRef) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    audioRef.current = ctx;
    const endTime = ctx.currentTime + 15;
    const playChime = (startTime) => {
      if (startTime > endTime) return;
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.setValueAtTime(523.25, startTime);       // C5
      osc.frequency.setValueAtTime(783.99, startTime + 0.25); // G5
      gain.gain.setValueAtTime(0.4, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.6);
      osc.start(startTime); osc.stop(startTime + 0.6);
      playChime(startTime + 1.4);
    };
    playChime(ctx.currentTime);
  } catch (e) { console.log('Audio error:', e); }
}
function stopPrescriptionRingtone(audioRef) {
  try { if (audioRef.current) { audioRef.current.close(); audioRef.current = null; } } catch (e) {}
}

// ── PATIENT INFO CARD ─────────────────────────────────────────────────────────
function PatientInfoCard({ patientInfo }) {
  if (!patientInfo) return null;
  return (
    <div style={{ backgroundColor: '#F0FDF4', borderRadius: 8, padding: 12, marginBottom: 10, border: '1px solid #BBF7D0' }}>
      <p style={{ fontSize: 13, fontWeight: 700, color: '#1D6B52', margin: '0 0 8px' }}>🏥 Patient Information</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
        {patientInfo.whoIsItFor && <p style={{ fontSize: 12, color: '#555', margin: 0 }}>👤 For: <strong>{patientInfo.whoIsItFor}</strong></p>}
        {patientInfo.age        && <p style={{ fontSize: 12, color: '#555', margin: 0 }}>🎂 Age: <strong>{patientInfo.age}</strong></p>}
        {patientInfo.gender     && <p style={{ fontSize: 12, color: '#555', margin: 0 }}>⚥ Gender: <strong>{patientInfo.gender}</strong></p>}
        {patientInfo.weight     && <p style={{ fontSize: 12, color: '#555', margin: 0 }}>⚖️ Weight: <strong>{patientInfo.weight}kg</strong></p>}
      </div>
      {patientInfo.allergies && patientInfo.allergies !== 'none' && patientInfo.allergies !== '' && (
        <p style={{ fontSize: 12, color: '#B45309', margin: '6px 0 0', fontWeight: 600 }}>⚠️ Allergies: {patientInfo.allergies}</p>
      )}
      {patientInfo.existingConditions && patientInfo.existingConditions !== 'none' && patientInfo.existingConditions !== '' && (
        <p style={{ fontSize: 12, color: '#7C3AED', margin: '4px 0 0', fontWeight: 600 }}>🩺 Conditions: {patientInfo.existingConditions}</p>
      )}
      {patientInfo.currentMedications && patientInfo.currentMedications !== 'none' && patientInfo.currentMedications !== '' && (
        <p style={{ fontSize: 12, color: '#1D4ED8', margin: '4px 0 0' }}>💊 Current meds: {patientInfo.currentMedications}</p>
      )}
      {patientInfo.pregnant && patientInfo.pregnant !== 'no' && (
        <p style={{ fontSize: 12, color: '#BE185D', margin: '4px 0 0', fontWeight: 600 }}>
          🤱 {patientInfo.pregnant === 'pregnant' ? 'Patient is pregnant' : 'Patient is breastfeeding'}
        </p>
      )}
      {patientInfo.notes && patientInfo.notes !== '' && (
        <p style={{ fontSize: 12, color: '#555', margin: '6px 0 0', fontStyle: 'italic' }}>📝 Note: {patientInfo.notes}</p>
      )}
    </div>
  );
}

// ── NEW ORDER BANNER ──────────────────────────────────────────────────────────
function NewOrderBanner({ newOrders, onView, onDismiss }) {
  if (newOrders.length === 0) return null;
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, backgroundColor: '#1D9E75', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <span style={{ fontSize: 28 }}>🔔</span>
        <div>
          <p style={{ color: '#FFF', fontWeight: 700, fontSize: 16, margin: 0 }}>{newOrders.length} New Order{newOrders.length > 1 ? 's' : ''}!</p>
          <p style={{ color: '#A8EDCF', fontSize: 13, margin: 0 }}>{newOrders.map(o => `#${o.id.slice(-6).toUpperCase()}`).join(', ')}</p>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button style={{ padding: '8px 18px', backgroundColor: '#FFF', color: '#1D9E75', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14 }} onClick={onView}>View Orders</button>
        <button style={{ padding: '8px 14px', backgroundColor: 'rgba(255,255,255,0.2)', color: '#FFF', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 8, cursor: 'pointer', fontSize: 14 }} onClick={onDismiss}>✕</button>
      </div>
    </div>
  );
}

// ── VERIFICATION MODAL (PCG/NEPP compliance gate) ──────────────────────────
const BACKEND_URL = 'https://pharmdev-rider.onrender.com';
const CHECKLIST_ITEMS = [
  { key: 'legible',              label: 'Prescription is legible and dated within the last 6 months' },
  { key: 'notExpired',           label: 'Prescription has not expired' },
  { key: 'doctorDetailsPresent', label: 'Doctor name, signature and stamp are present' },
  { key: 'dosageMatches',        label: 'Dosage and frequency match what was ordered' },
  { key: 'controlledDrugChecks', label: 'For controlled drugs: original prescription, not a refill beyond limit' },
  { key: 'ageAppropriate',       label: 'Patient age is appropriate for this medicine' },
];
const REJECTION_REASONS = [
  { value: 'blurry',          label: 'Blurry / illegible prescription' },
  { value: 'expired',         label: 'Prescription expired' },
  { value: 'no_prescription', label: 'No prescription provided' },
  { value: 'needs_original',  label: 'Needs the original (not a copy)' },
  { value: 'other',           label: 'Other' },
];

async function complianceFetch(path, body) {
  const token = await auth.currentUser.getIdToken();
  const res = await fetch(`${BACKEND_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

function VerificationModal({ order, pharmacyId, onClose }) {
  const [pharmacists, setPharmacists] = useState([]);
  const [checklist, setChecklist]     = useState({});
  const [coldChain, setColdChain]     = useState(false);
  const [note, setNote]               = useState('');
  const [pharmacistId, setPharmacistId] = useState('');
  const [pin, setPin]                 = useState('');
  const [step, setStep]               = useState('checklist'); // 'checklist' | 'reject'
  const [rejectReason, setRejectReason] = useState('');
  const [rejectNote, setRejectNote]   = useState('');
  const [busy, setBusy]               = useState(false);
  const [prescription, setPrescription] = useState(null);

  const needsColdChain = order.items?.some(i => i.coldChain);
  const allChecked = CHECKLIST_ITEMS.every(item => checklist[item.key]);

  useEffect(() => {
    return onValue(ref(db, `pharmacies/${pharmacyId}/pharmacists`), snap => {
      const list = snap.exists() ? Object.entries(snap.val()).map(([id, p]) => ({ id, ...p })).filter(p => p.active !== false) : [];
      setPharmacists(list);
      if (list.length === 1) setPharmacistId(list[0].id);
    });
  }, [pharmacyId]);

  useEffect(() => {
    if (!order.prescriptionId) return;
    onValue(ref(db, `prescriptions/${order.prescriptionId}`), snap => setPrescription(snap.val()), { onlyOnce: true });
  }, [order.prescriptionId]);

  const toggle = (key) => setChecklist(c => ({ ...c, [key]: !c[key] }));

  const submitVerification = async () => {
    if (!allChecked) { alert('Every checklist item must be confirmed.'); return; }
    if (!pharmacistId) { alert('Select which pharmacist is verifying this.'); return; }
    if (!/^\d{4}$/.test(pin)) { alert('Enter the pharmacist\'s 4-digit PIN.'); return; }
    setBusy(true);
    try {
      const pinResult = await complianceFetch('/pharmacy/pharmacist/verify-pin', { pharmacistId, pin });
      await complianceFetch('/pharmacy/verify-prescription', {
        orderId: order.id, checklist, coldChainConfirmed: coldChain, counselingNote: note,
        pharmacistToken: pinResult.token,
      });
      alert(`✅ Verified by ${pinResult.name} and submitted to NEPP.`);
      onClose();
    } catch (e) {
      alert(e.message || 'Verification failed.');
    } finally {
      setBusy(false);
    }
  };

  const submitReject = async () => {
    if (!rejectReason) { alert('Select a rejection reason.'); return; }
    setBusy(true);
    try {
      await complianceFetch('/pharmacy/reject-prescription', {
        orderId: order.id, prescriptionId: order.prescriptionId || null, reason: rejectReason, note: rejectNote,
      });
      alert('Order rejected and the customer has been notified.');
      onClose();
    } catch (e) {
      alert(e.message || 'Could not reject.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ backgroundColor: '#FFF', borderRadius: 16, padding: 24, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontWeight: 700, margin: 0, fontSize: 17 }}>
            {step === 'checklist' ? '🔒 Verify Prescription' : '✕ Reject Prescription'}
          </h3>
          <button style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }} onClick={onClose}>✕</button>
        </div>

        {prescription?.imageData && (
          <img src={prescription.imageData} alt="Prescription" style={{ width: '100%', maxHeight: 220, objectFit: 'contain', borderRadius: 10, border: '1px solid #E5E7EB', marginBottom: 16 }} />
        )}

        {step === 'checklist' ? (
          <>
            <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
              {CHECKLIST_ITEMS.map(item => (
                <label key={item.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!checklist[item.key]} onChange={() => toggle(item.key)} style={{ width: 16, height: 16, marginTop: 2, accentColor: '#1D9E75' }} />
                  <span style={{ fontSize: 13, color: '#333' }}>{item.label}</span>
                </label>
              ))}
              {needsColdChain && (
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                  <input type="checkbox" checked={coldChain} onChange={() => setColdChain(v => !v)} style={{ width: 16, height: 16, marginTop: 2, accentColor: '#3B82F6' }} />
                  <span style={{ fontSize: 13, color: '#333' }}>❄️ Cold box confirmed for this medicine</span>
                </label>
              )}
            </div>

            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Patient counseling note (shown on invoice)</label>
            <textarea style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid #E5E7EB', fontSize: 13, marginBottom: 16, minHeight: 60, boxSizing: 'border-box' }}
              placeholder="e.g. Take after food, twice daily for 5 days" value={note} onChange={e => setNote(e.target.value)} />

            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Verifying Pharmacist</label>
            <select style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid #E5E7EB', fontSize: 13, marginBottom: 10, boxSizing: 'border-box' }} value={pharmacistId} onChange={e => setPharmacistId(e.target.value)}>
              <option value="">Select pharmacist…</option>
              {pharmacists.map(p => <option key={p.id} value={p.id}>{p.name} (PCG {p.pcgRegNo})</option>)}
            </select>
            {pharmacists.length === 0 && (
              <p style={{ fontSize: 12, color: '#B91C1C', margin: '0 0 10px' }}>No pharmacists on file yet — add one under the Pharmacists tab first.</p>
            )}
            <input type="password" inputMode="numeric" maxLength={4} placeholder="4-digit PIN" value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ''))}
              style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid #E5E7EB', fontSize: 13, marginBottom: 16, boxSizing: 'border-box' }} />

            <button
              style={{ width: '100%', padding: 12, backgroundColor: (!allChecked || busy) ? '#A8D5C2' : '#1D9E75', color: '#FFF', border: 'none', borderRadius: 10, cursor: (!allChecked || busy) ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 14, marginBottom: 8 }}
              onClick={submitVerification}
              disabled={!allChecked || busy}
            >
              {busy ? 'Submitting to NEPP…' : '✓ Verified & Packed'}
            </button>
            <button style={{ width: '100%', padding: 10, backgroundColor: '#FFF', color: '#B91C1C', border: '1px solid #FCA5A5', borderRadius: 10, cursor: 'pointer', fontSize: 13 }} onClick={() => setStep('reject')}>
              ✕ Reject this prescription instead
            </button>
          </>
        ) : (
          <>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Reason</label>
            <select style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid #E5E7EB', fontSize: 13, marginBottom: 12, boxSizing: 'border-box' }} value={rejectReason} onChange={e => setRejectReason(e.target.value)}>
              <option value="">Select a reason…</option>
              {REJECTION_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Note (optional, shown to customer)</label>
            <textarea style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid #E5E7EB', fontSize: 13, marginBottom: 16, minHeight: 60, boxSizing: 'border-box' }}
              value={rejectNote} onChange={e => setRejectNote(e.target.value)} />
            <button
              style={{ width: '100%', padding: 12, backgroundColor: busy ? '#F5A9A9' : '#B91C1C', color: '#FFF', border: 'none', borderRadius: 10, cursor: busy ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 14, marginBottom: 8 }}
              onClick={submitReject}
              disabled={busy}
            >
              {busy ? 'Rejecting…' : '✕ Reject Order'}
            </button>
            <button style={{ width: '100%', padding: 10, backgroundColor: '#FFF', color: '#555', border: '1px solid #E5E7EB', borderRadius: 10, cursor: 'pointer', fontSize: 13 }} onClick={() => setStep('checklist')}>
              ← Back to verification
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── RECEIPT MODAL ─────────────────────────────────────────────────────────────
function ReceiptModal({ order, pharmacyData, onClose }) {
  const printReceipt = () => {
    const win = window.open('', '_blank');
    if (!win) {
      alert('Could not open the receipt — please allow pop-ups for this site and try again.');
      return;
    }
    win.document.write(`
      <html><head><title>PharmDev Receipt</title>
      <style>body{font-family:Arial,sans-serif;padding:20px;max-width:400px;margin:0 auto}
      h2{color:#1D9E75;text-align:center}.divider{border:none;border-top:1px solid #eee;margin:10px 0}
      .row{display:flex;justify-content:space-between;margin:4px 0;font-size:13px}
      .total{font-weight:700;font-size:15px}.med-block{background:#f0fdf4;border-radius:6px;padding:8px;margin:6px 0;font-size:12px}
      </style></head><body>
      <h2>💊 PharmDev</h2>
      <p style="text-align:center;font-size:12px;color:#888">${escapeHtml(pharmacyData?.pharmacyName || 'Pharmacy')} · ${escapeHtml(pharmacyData?.location || '')}</p>
      <hr class="divider">
      <div class="row"><span>Order #</span><strong>${escapeHtml((order.orderId || order.id).slice(-6).toUpperCase())}</strong></div>
      <div class="row"><span>Date</span><span>${escapeHtml(new Date(order.createdAt).toLocaleString())}</span></div>
      <div class="row"><span>Payment</span><span>${escapeHtml(order.paymentMethod || 'MoMo')}</span></div>
      <hr class="divider">
      <p style="font-weight:700;font-size:13px;margin:8px 0 4px">Customer</p>
      <div class="row"><span>Name</span><span>${escapeHtml(order.customerName || '—')}</span></div>
      <div class="row"><span>Phone</span><span>${escapeHtml(order.customerPhone || '—')}</span></div>
      <div class="row"><span>Address</span><span>${escapeHtml(order.deliveryAddress || '—')}</span></div>
      ${order.ghanaPostGPS ? `<div class="row"><span>Ghana Post GPS</span><span>${escapeHtml(order.ghanaPostGPS)}</span></div>` : ''}
      <hr class="divider">
      <p style="font-weight:700;font-size:13px;margin:8px 0 4px">Medicines</p>
      ${(order.items || []).map(item => `
        <div class="med-block">
          <div class="row"><strong>${escapeHtml(item.name)}</strong><span>x${item.quantity} = GH₵ ${((item.customerPrice || item.price) * item.quantity).toFixed(2)}</span></div>
          ${item.dosage?.amount ? `<div style="color:#1D6B52;margin-top:4px">💊 ${escapeHtml(item.dosage.amount)} · ${escapeHtml(item.dosage.frequency)} · ${escapeHtml(item.dosage.timing)}</div>` : ''}
          ${item.warnings ? `<div style="color:#92400E;margin-top:2px">⚠️ ${escapeHtml(item.warnings)}</div>` : ''}
        </div>`).join('')}
      ${order.verification ? `
      <hr class="divider">
      <p style="font-weight:700;font-size:13px;margin:8px 0 4px">Compliance</p>
      <div class="row"><span>PCG License</span><span>${escapeHtml(pharmacyData?.pcgLicenseNumber || pharmacyData?.licenseNumber || '—')}</span></div>
      <div class="row"><span>Verified by</span><span>${escapeHtml(order.verification.pharmacistName)} (${escapeHtml(order.verification.pharmacistRegNo)})</span></div>
      <div class="row"><span>NEPP Verified</span><span>ID ${escapeHtml(order.nepp?.transactionId || '—')}${order.nepp?.stub ? ' (test)' : ''}</span></div>
      ${order.counselingNote ? `<div style="margin-top:6px;color:#1D6B52"><strong>Counseling:</strong> ${escapeHtml(order.counselingNote)}</div>` : ''}
      ` : ''}
      <hr class="divider">
      <div class="row"><span>Subtotal</span><span>GH₵ ${(order.customerSubtotal || order.subtotal || (order.total - (order.deliveryFee || 0) - (order.serviceFee || 0))).toFixed(2)}</span></div>
      <div class="row"><span>Delivery fee</span><span>GH₵ ${(order.deliveryFee || 0).toFixed(2)}</span></div>
      ${order.serviceFee ? `<div class="row"><span>Service fee</span><span>GH₵ ${order.serviceFee.toFixed(2)}</span></div>` : ''}
      <div class="row total"><span>TOTAL PAID</span><span>GH₵ ${order.total?.toFixed(2)}</span></div>
      <hr class="divider">
      <p style="text-align:center;font-size:11px;color:#888;margin-top:16px">Thank you for using PharmDev 💊<br>pharmdev.org@gmail.com</p>
      </body></html>`);
    win.document.close(); win.print();
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ backgroundColor: '#FFF', borderRadius: 16, padding: 28, maxWidth: 500, width: '100%', maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontWeight: 700, margin: 0, fontSize: 17 }}>🧾 Order Receipt</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ padding: '8px 16px', backgroundColor: '#1D9E75', color: '#FFF', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }} onClick={printReceipt}>🖨️ Print</button>
            <button style={{ padding: '8px 14px', backgroundColor: '#F7F8FA', color: '#555', border: '1px solid #E5E7EB', borderRadius: 8, cursor: 'pointer', fontSize: 13 }} onClick={onClose}>✕ Close</button>
          </div>
        </div>
        <div style={{ backgroundColor: '#F7F8FA', borderRadius: 10, padding: 16, fontSize: 13 }}>
          <p style={{ fontWeight: 700, color: '#1D9E75', margin: '0 0 8px', textAlign: 'center' }}>💊 PharmDev · {pharmacyData?.pharmacyName}</p>
          {[['Order #', (order.orderId || order.id).slice(-6).toUpperCase()], ['Date', new Date(order.createdAt).toLocaleString()], ['Customer', order.customerName], ['Phone', order.customerPhone || '—'], ['Address', order.deliveryAddress], ['Ghana Post GPS', order.ghanaPostGPS || '—'], ['Payment', order.paymentMethod || 'MoMo']].map(([l, v]) => (
            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '0.5px solid #E5E7EB', padding: '5px 0' }}>
              <span style={{ color: '#888' }}>{l}</span>
              <span style={{ fontWeight: 500, maxWidth: '60%', textAlign: 'right' }}>{v}</span>
            </div>
          ))}
          <p style={{ fontWeight: 700, margin: '12px 0 6px' }}>Medicines Dispensed</p>
          {order.items?.map((item, i) => (
            <div key={i} style={{ backgroundColor: '#E8F8F2', borderRadius: 8, padding: 10, marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <strong style={{ fontSize: 13 }}>{item.name}</strong>
                <span style={{ fontSize: 13 }}>x{item.quantity} = GH₵ {((item.customerPrice || item.price) * item.quantity).toFixed(2)}</span>
              </div>
              {item.dosage?.amount && <p style={{ fontSize: 11, color: '#1D6B52', margin: '4px 0 0' }}>💊 {item.dosage.amount} · {item.dosage.frequency} · {item.dosage.timing}</p>}
              {item.warnings && <p style={{ fontSize: 11, color: '#92400E', margin: '3px 0 0' }}>⚠️ {item.warnings}</p>}
            </div>
          ))}
          {order.verification && (
            <div style={{ backgroundColor: '#F0FDF4', border: '1px solid #B2DFD0', borderRadius: 8, padding: 10, marginBottom: 6 }}>
              <p style={{ fontWeight: 700, fontSize: 12, color: '#1D6B52', margin: '0 0 4px' }}>✅ Compliance</p>
              <p style={{ fontSize: 11, color: '#1D6B52', margin: '2px 0' }}>PCG License: {pharmacyData?.pcgLicenseNumber || pharmacyData?.licenseNumber || '—'}</p>
              <p style={{ fontSize: 11, color: '#1D6B52', margin: '2px 0' }}>Verified by: {order.verification.pharmacistName} ({order.verification.pharmacistRegNo})</p>
              <p style={{ fontSize: 11, color: '#1D6B52', margin: '2px 0' }}>NEPP Verified: ID {order.nepp?.transactionId || '—'}{order.nepp?.stub ? ' (test)' : ''}</p>
              {order.counselingNote && <p style={{ fontSize: 11, color: '#1D6B52', margin: '4px 0 0' }}>💬 {order.counselingNote}</p>}
            </div>
          )}
          <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: 8, marginTop: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}><span>Delivery fee</span><span>GH₵ {(order.deliveryFee || 0).toFixed(2)}</span></div>
            {!!order.serviceFee && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}><span>Service fee</span><span>GH₵ {order.serviceFee.toFixed(2)}</span></div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 15 }}><span>TOTAL</span><span style={{ color: '#1D9E75' }}>GH₵ {order.total?.toFixed(2)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── ORDER CARD ────────────────────────────────────────────────────────────────
function OrderCard({ order, updating, onUpdate, isNew, pharmacyData, pharmacyId }) {
  const [expanded, setExpanded]       = useState(isNew);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showVerify, setShowVerify]   = useState(false);
  const needsVerification = order.drugSchedule && order.drugSchedule !== 'otc' && order.status !== 'cancelled';
  const isVerified = !!order.verification;

  return (
    <div style={{ ...s.orderCard, border: isNew ? '2px solid #1D9E75' : '1px solid #E5E7EB', position: 'relative' }}>
      {isNew && (
        <div style={{ position: 'absolute', top: -10, right: 16, backgroundColor: '#1D9E75', color: '#FFF', fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 20 }}>
          🆕 NEW
        </div>
      )}
      {showReceipt && <ReceiptModal order={order} pharmacyData={pharmacyData} onClose={() => setShowReceipt(false)} />}
      {showVerify && <VerificationModal order={order} pharmacyId={pharmacyId} onClose={() => setShowVerify(false)} />}

      <div style={s.orderCardHeader}>
        <div>
          <p style={s.orderId}>#{order.orderId?.slice(-6).toUpperCase() || order.id.slice(-6).toUpperCase()}</p>
          <p style={s.orderTime}>{new Date(order.createdAt).toLocaleString()}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ ...s.statusBadge, backgroundColor: (STATUS_COLORS[order.status] || '#888') + '20', color: STATUS_COLORS[order.status] || '#888' }}>
            {STATUS_ICONS[order.status]} {order.status}
          </span>
          <button style={{ padding: '4px 10px', backgroundColor: '#F0FDF4', color: '#1D9E75', border: '1px solid #B2DFD0', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }} onClick={() => setShowReceipt(true)}>
            🧾 Receipt
          </button>
        </div>
      </div>

      <div style={s.orderCustomer}>
        <p style={s.customerName}>👤 {order.customerName || 'Customer'}</p>
        <p style={s.customerEmail}>{order.customerEmail}</p>
        {order.customerPhone
          ? <a href={`tel:${order.customerPhone}`} style={s.customerPhone}>📞 {order.customerPhone} <span style={s.callBtn}>Call customer</span></a>
          : <p style={{ fontSize: 12, color: '#BBB', margin: '2px 0 0' }}>No phone on file</p>
        }
      </div>

      {needsVerification && (
        <div style={{
          backgroundColor: isVerified ? '#F0FDF4' : '#FEF3C7',
          border: `1px solid ${isVerified ? '#B2DFD0' : '#FCD34D'}`,
          borderRadius: 8, padding: 12, marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <p style={{ fontWeight: 700, fontSize: 13, margin: 0, color: isVerified ? '#1D6B52' : '#92400E' }}>
              {order.drugSchedule === 'controlled' ? '⚫ Controlled' : '🔴 POM'} — {isVerified ? '✅ Verified & Packed' : '🔒 Verification required before dispatch'}
            </p>
            {isVerified && (
              <p style={{ fontSize: 12, color: '#1D9E75', margin: '4px 0 0' }}>
                {order.verification.pharmacistName} (PCG {order.verification.pharmacistRegNo}) · NEPP {order.nepp?.transactionId}{order.nepp?.stub ? ' (stub)' : ''}
              </p>
            )}
          </div>
          {!isVerified && (
            <button
              style={{ padding: '8px 14px', backgroundColor: '#1D9E75', color: '#FFF', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
              onClick={() => setShowVerify(true)}
            >
              Verify Prescription
            </button>
          )}
        </div>
      )}

      <PatientInfoCard patientInfo={order.patientInfo} />

      {/* ── RIDER ASSIGNED CARD ── */}
      {order.riderName && ['dispatched', 'delivering'].includes(order.status) && (
        <div style={{ backgroundColor: '#EFF6FF', borderRadius: 8, padding: 12, marginBottom: 10, border: '1px solid #BFDBFE' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <RiderPhoto riderId={order.riderId} name={order.riderName} />
          <div style={{ flex: 1 }}>
            <p style={{ fontWeight: 700, fontSize: 13, color: '#1D4ED8', margin: '0 0 2px' }}>
              {order.status === 'dispatched' ? 'Rider on the way to pick up' : 'Rider out for delivery'}
            </p>
            <p style={{ fontSize: 12, color: '#3B82F6', margin: 0 }}>
              🛵 {order.riderName}{order.riderPhone ? ` · 📞 ${order.riderPhone}` : ''}
            </p>
          </div>
          {order.riderPhone && (
            <a href={`tel:${order.riderPhone}`} style={{ backgroundColor: '#3B82F6', color: '#FFF', padding: '6px 12px', borderRadius: 8, textDecoration: 'none', fontSize: 12, fontWeight: 600 }}>
              📞 Call
            </a>
          )}
        </div>
        {order.status === 'dispatched' && <PickupCodeBox order={order} />}
        </div>
      )}

      <div style={s.orderItems}>
        {order.items?.map((item, i) => (
          <div key={i}>
            <div style={s.orderItem}>
              <span style={s.itemName}>{item.name}</span>
              <span style={s.itemDetail}>x{item.quantity} · GH₵ {((item.customerPrice || item.price) * item.quantity).toFixed(2)}</span>
            </div>
            {item.dosage?.amount && expanded && (
              <div style={{ backgroundColor: '#F0FDF4', borderRadius: 6, padding: 8, marginBottom: 6, fontSize: 12, color: '#1D6B52' }}>
                💊 <strong>Dosage:</strong> {item.dosage.amount} · {item.dosage.frequency} · {item.dosage.timing}
                {item.dosage.adult && <span> | Adult: {item.dosage.adult}</span>}
                {item.dosage.child && <span> | Child: {item.dosage.child}</span>}
                {item.dosage.times?.length > 0 && <span> | Times: {item.dosage.times.join(', ')}</span>}
              </div>
            )}
            {item.warnings && expanded && (
              <div style={{ backgroundColor: '#FEF3C7', borderRadius: 6, padding: 8, marginBottom: 6, fontSize: 12, color: '#92400E' }}>
                ⚠️ {item.warnings}
              </div>
            )}
          </div>
        ))}
      </div>

      {order.items?.some(i => i.substituteAccepted) && (
        <div style={{ backgroundColor: '#EFF6FF', borderRadius: 8, padding: 10, marginBottom: 8, border: '1px solid #BFDBFE' }}>
          <p style={{ fontSize: 12, color: '#1D4ED8', fontWeight: 600, margin: 0 }}>
            💡 Customer accepted a substitute medicine — please verify before dispensing
          </p>
        </div>
      )}

      <div style={s.deliveryAddress}>
        <p style={{ margin: 0, fontSize: 13, color: '#555' }}>📍 {order.deliveryAddress || 'Address not provided'}</p>
        {order.ghanaPostGPS && (
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#1D9E75', fontWeight: 600 }}>🇬🇭 Ghana Post GPS: {order.ghanaPostGPS}</p>
        )}
      </div>

      <button style={{ fontSize: 12, color: '#888', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0 8px', display: 'block' }} onClick={() => setExpanded(!expanded)}>
        {expanded ? '▲ Hide dosage details' : '▼ Show dosage & warnings'}
      </button>

      <div style={s.orderFooter}>
        <div style={s.orderTotal}>
          <span style={s.totalLabel}>Total</span>
          <span style={s.totalValue}>GH₵ {order.total?.toFixed(2)}</span>
        </div>
        <div style={s.actionRow}>
          <select
            style={{ ...s.statusSelect, ...(needsVerification && !isVerified ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
            value={order.status}
            onChange={e => onUpdate(order.id, e.target.value)}
            disabled={updating === order.id || (needsVerification && !isVerified && order.status === 'preparing')}
          >
            {STATUS_OPTIONS.map(st => <option key={st} value={st}>{st}</option>)}
          </select>
          <button
            style={{ ...s.updateBtn, opacity: (updating === order.id || (needsVerification && !isVerified && order.status === 'preparing')) ? 0.6 : 1 }}
            onClick={() => onUpdate(order.id, order.status)}
            disabled={updating === order.id || (needsVerification && !isVerified && order.status === 'preparing')}
            title={needsVerification && !isVerified ? 'Complete prescription verification first' : undefined}
          >
            {updating === order.id ? '...' : '✓ Update'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── PENDING PAGE ──────────────────────────────────────────────────────────────
function PendingPage({ onLogout }) {
  return (
    <div style={s.authPage}>
      <div style={s.authCard}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
        <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Application under review</h2>
        <p style={{ color: '#666', fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
          Our team is reviewing your pharmacy license. You will receive an email within 24 hours once approved.
        </p>
        <div style={s.infoBox}>
          <p style={s.infoText}>📧 Contact: <strong>pharmdev.org@gmail.com</strong></p>
          <p style={s.infoText}>📞 055 190 6880 · 059 984 8708</p>
        </div>
        <button style={{ ...s.loginBtn, backgroundColor: '#EF4444', marginTop: 24 }} onClick={onLogout}>Log out</button>
      </div>
    </div>
  );
}

// ── DASHBOARD ─────────────────────────────────────────────────────────────────
function Dashboard({ onLogout, pharmacyId, pharmacyData }) {
  const [orders, setOrders]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [filter, setFilter]               = useState('all');
  const [activeTab, setActiveTab]         = useState('orders');
  const [tabsOpen, setTabsOpen]           = useState(true);
  const [updating, setUpdating]           = useState(null);
  const [showGuide, setShowGuide]         = useState(!localStorage.getItem('guideShown'));
  const [newOrders, setNewOrders]         = useState([]);
  const [showNewBanner, setShowNewBanner] = useState(false);
  const [seenOrderIds, setSeenOrderIds]   = useState(new Set());
  // The orders listener effect below only re-subscribes when pharmacyId
  // changes, so its onValue callback closure would otherwise always see the
  // seenOrderIds value from mount time — never the latest state — making
  // every already-seen pending order look "fresh" again on every unrelated
  // order update and re-firing the new-order ringtone/banner for old orders.
  // Reading through this ref instead keeps the callback looking at the
  // current value without needing seenOrderIds as an effect dependency
  // (which would itself thrash the subscription on every new order).
  const seenOrderIdsRef = useRef(new Set());
  useEffect(() => { seenOrderIdsRef.current = seenOrderIds; }, [seenOrderIds]);
  const [pendingPrescriptions, setPendingPrescriptions] = useState(0);
  const [unreadChats, setUnreadChats]     = useState(0);
  const [unreadAnnouncements, setUnreadAnnouncements] = useState(0);
  const [riderAlerts, setRiderAlerts]     = useState([]);
  const [isOpen, setIsOpen]               = useState(pharmacyData?.isOpen !== false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
    };
    const handleOffline = () => {
      setIsOnline(false);
      update(ref(db, `pharmacies/${pharmacyId}`), { isOpen: false }).catch(() => {});
      update(ref(db, `pharmacies_map/${pharmacyId}`), { isOpen: false }).catch(() => {});
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [pharmacyId]);
  const [darkMode, setDarkMode]           = useState(localStorage.getItem('darkMode') === '1');
  const audioRef         = useRef(null);
  const ringtoneTimerRef = useRef(null);
  const isFirstLoad      = useRef(true);
  const prescriptionAudioRef        = useRef(null);
  const prescriptionRingtoneTimerRef = useRef(null);
  const seenPrescriptionIds          = useRef(new Set());
  const isPrescriptionFirstLoad      = useRef(true);

  const toggleDarkMode = () => {
    const next = !darkMode;
    setDarkMode(next);
    document.body.classList.toggle('dark-mode', next);
    localStorage.setItem('darkMode', next ? '1' : '0');
  };

  // 45-minute pending-order reassignment used to run here client-side (and
  // duplicated in the admin dashboard too, racing whichever tab happened to
  // be open — plus a bug where it wrote `nextPharmacy.name`, which doesn't
  // exist on a pharmacy record, instead of `nextPharmacy.pharmacyName`).
  // It's now handled authoritatively by the render-backend's
  // /orders/maintenance job, called every 5 minutes by GitHub Actions.

  // ── ORDERS LISTENER ───────────────────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = onValue(ref(db, `pharmacyOrders/${pharmacyId}`), snap => {
      if (snap.exists()) {
        const all = Object.entries(snap.val())
          .map(([k, v]) => ({ id: k, ...v }))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setOrders(all);

        if (!isFirstLoad.current) {
          // Stop ringtone when order moves to preparing
          const nowPreparing = all.filter(o =>
            o.status === 'preparing' && seenOrderIdsRef.current.has(o.id)
          );
          if (nowPreparing.length > 0) {
            stopOrderRingtone(audioRef);
            if (ringtoneTimerRef.current) clearTimeout(ringtoneTimerRef.current);
          }

          const fresh = all.filter(o => o.status === 'pending' && !seenOrderIdsRef.current.has(o.id));
          if (fresh.length > 0) {
            setNewOrders(fresh);
            setShowNewBanner(true);
            fresh.forEach(o => setSeenOrderIds(prev => new Set([...prev, o.id])));
            playOrderRingtone(audioRef);
            if (ringtoneTimerRef.current) clearTimeout(ringtoneTimerRef.current);
            ringtoneTimerRef.current = setTimeout(() => stopOrderRingtone(audioRef), 15000);
          }
        } else {
          setSeenOrderIds(new Set(all.map(o => o.id)));
          isFirstLoad.current = false;
        }
      } else { setOrders([]); isFirstLoad.current = false; }
      setLoading(false);
    });
    return () => {
      unsubscribe();
      stopOrderRingtone(audioRef);
      if (ringtoneTimerRef.current) clearTimeout(ringtoneTimerRef.current);
    };
  }, [pharmacyId]);

  // ── PRESCRIPTIONS UNREAD COUNT + RINGTONE ─────────────────────────────────
  // Prescriptions are deliberately created with no pharmacyId — every
  // pharmacy can see and quote an unclaimed one (PrescriptionScreen.js:
  // "shows to all pharmacies") — so this can't be scoped to `this` pharmacy
  // the way orders/medicines are. It CAN be scoped to status==='pending'
  // though: that's the only status an unclaimed prescription is ever in
  // (quoting sets pharmacyId + moves it to 'quoted'), so querying just the
  // pending ones is both correct and keeps the payload bounded by
  // work-in-flight instead of by all-time prescription history.
  useEffect(() => {
    const unsubscribe = onValue(query(ref(db, 'prescriptions'), orderByChild('status'), equalTo('pending')), snap => {
      if (!snap.exists()) { setPendingPrescriptions(0); return; }
      const pending = Object.entries(snap.val()).map(([id, p]) => ({ id, ...p }));
      setPendingPrescriptions(pending.length);

      if (!isPrescriptionFirstLoad.current) {
        const fresh = pending.filter(p => !seenPrescriptionIds.current.has(p.id));
        if (fresh.length > 0) {
          fresh.forEach(p => seenPrescriptionIds.current.add(p.id));
          playPrescriptionRingtone(prescriptionAudioRef);
          if (prescriptionRingtoneTimerRef.current) clearTimeout(prescriptionRingtoneTimerRef.current);
          prescriptionRingtoneTimerRef.current = setTimeout(() => stopPrescriptionRingtone(prescriptionAudioRef), 15000);
        }
      } else {
        pending.forEach(p => seenPrescriptionIds.current.add(p.id));
        isPrescriptionFirstLoad.current = false;
      }
    });
    return () => {
      unsubscribe();
      stopPrescriptionRingtone(prescriptionAudioRef);
      if (prescriptionRingtoneTimerRef.current) clearTimeout(prescriptionRingtoneTimerRef.current);
    };
  }, [pharmacyId]);

  // ── UNREAD CHATS COUNT ────────────────────────────────────────────────────
  // Queried by pharmacyId server-side (see ChatTab.js) instead of pulling
  // every pharmacy's chats just to count this one's unread badge.
  useEffect(() => {
    if (!pharmacyId) return;
    onValue(query(ref(db, 'chats'), orderByChild('pharmacyId'), equalTo(pharmacyId)), snap => {
      if (snap.exists()) {
        const count = Object.values(snap.val()).filter(c =>
          (c.pharmacyId === pharmacyId || c.pharmacyName === pharmacyData?.pharmacyName) && c.unreadByPharmacy
        ).length;
        setUnreadChats(count);
      } else {
        setUnreadChats(0);
      }
    });
  }, [pharmacyId, pharmacyData?.pharmacyName]);

  // ── UNREAD ANNOUNCEMENTS COUNT ────────────────────────────────────────────
  useEffect(() => {
    onValue(ref(db, 'broadcasts'), snap => {
      const val = snap.val() || {};
      const total = Object.values(val).filter(b => b.target === 'all_pharmacies').length;
      const lastSeenId = localStorage.getItem(`announcements_seen_${pharmacyId}`);
      const latest = Object.entries(val)
        .filter(([, b]) => b.target === 'all_pharmacies')
        .sort(([, a], [, b]) => new Date(b.sentAt) - new Date(a.sentAt))[0];
      setUnreadAnnouncements(lastSeenId === latest?.[0] ? 0 : total > 0 ? 1 : 0);
    });
  }, [pharmacyId]);

  // ── RIDER ALERT LISTENER ──────────────────────────────────────────────────
  useEffect(() => {
    onValue(ref(db, `pharmacies/${pharmacyId}/riderAlert`), snap => {
      if (snap.exists()) {
        const alert = snap.val();
        const ageMinutes = (Date.now() - new Date(alert.sentAt)) / 60000;
        if (ageMinutes < 10) {
          setRiderAlerts(prev => {
            const exists = prev.find(a => a.orderId === alert.orderId);
            return exists ? prev : [alert, ...prev];
          });
        }
      }
    });
  }, [pharmacyId]);

  const dismissBanner = () => {
    setShowNewBanner(false); setNewOrders([]);
    stopOrderRingtone(audioRef);
    if (ringtoneTimerRef.current) clearTimeout(ringtoneTimerRef.current);
  };

  const viewNewOrders = () => {
    setShowNewBanner(false); setFilter('new'); setActiveTab('orders');
    stopOrderRingtone(audioRef);
    if (ringtoneTimerRef.current) clearTimeout(ringtoneTimerRef.current);
    window.scrollTo(0, 300);
  };

  const updateStatus = async (orderId, status) => {
    let cancelReason;
    if (status === 'cancelled') {
      cancelReason = window.prompt('Why is this order being cancelled? (shown to the customer)');
      if (cancelReason === null) return; // user backed out of the cancellation
    }
    setUpdating(orderId);
    try {
      await update(ref(db, `orders/${orderId}`), {
        status,
        updatedAt: new Date().toISOString(),
        ...(status === 'cancelled' ? {
          cancelReason: cancelReason || 'Cancelled by pharmacy',
          cancelledAt: new Date().toISOString(),
          cancelledBy: auth.currentUser?.email || 'pharmacy',
        } : {}),
      });
    } catch (e) {
      console.error(e);
      alert('Could not update order status. Please check your connection and try again.');
    }
    finally { setUpdating(null); }
  };

  const toggleOpen = async () => {
    const newVal = !isOpen;
    setIsOpen(newVal);
    await update(ref(db, `pharmacies/${pharmacyId}`), { isOpen: newVal });
    await update(ref(db, `pharmacies_map/${pharmacyId}`), { isOpen: newVal });
  };

  const counts = {
    all:        orders.length,
    pending:    orders.filter(o => o.status === 'pending').length,
    preparing:  orders.filter(o => o.status === 'preparing').length,
    dispatched: orders.filter(o => o.status === 'dispatched').length,
    delivering: orders.filter(o => o.status === 'delivering').length,
    delivered:  orders.filter(o => o.status === 'delivered').length,
  };

  const newOrderIds = new Set(newOrders.map(o => o.id));

  const filtered = (() => {
    if (filter === 'new') return orders.filter(o => newOrderIds.has(o.id));
    if (filter === 'all') return orders;
    return orders.filter(o => o.status === filter);
  })();

  const bannerOffset = (showNewBanner ? 72 : 0) + (riderAlerts.length > 0 ? riderAlerts.length * 60 : 0);

  const tabs = [
    { id: 'orders',        label: `📦 Orders (${counts.all})` },
    { id: 'prescriptions', label: `📋 Prescriptions${pendingPrescriptions > 0 ? ` (${pendingPrescriptions})` : ''}` },
    { id: 'chat',          label: `💬 Customer Chat${unreadChats > 0 ? ` (${unreadChats})` : ''}` },
    { id: 'inventory',     label: '💊 Inventory' },
    { id: 'pharmacists',   label: '🧑‍⚕️ Pharmacists' },
    { id: 'sales',         label: '📈 Sales' },
    { id: 'earnings',      label: '💰 Earnings' },
    { id: 'hours',         label: '🕐 Hours' },
    { id: 'profile',       label: '🏪 Profile' },
    { id: 'bank_account', label: '🏦 Bank Account' },
    { id: 'announcements', label: `📣 Announcements${unreadAnnouncements > 0 ? ' 🔴' : ''}` },
  ];

  return (
    <div style={s.dashboard}>
      {showGuide && <UserGuide onClose={() => { localStorage.setItem('guideShown', 'true'); setShowGuide(false); }} />}

      {/* NEW ORDER BANNER */}
      {showNewBanner && <NewOrderBanner newOrders={newOrders} onView={viewNewOrders} onDismiss={dismissBanner} />}

      {/* RIDER ALERT BANNERS */}
      {riderAlerts.map((alert, i) => (
        <div key={i} style={{ position: 'fixed', top: showNewBanner ? 72 + (i * 62) : i * 62, left: 0, right: 0, zIndex: 9998, backgroundColor: '#3B82F6', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {alert.riderId ? <RiderPhoto riderId={alert.riderId} name={alert.riderName} size={40} /> : <span style={{ fontSize: 24 }}>🛵</span>}
            <div>
              <p style={{ color: '#FFF', fontWeight: 700, fontSize: 15, margin: 0 }}>
                Rider on the way! — {alert.orderRefs && alert.orderRefs.length > 1 ? `Orders ${alert.orderRefs.map(r => '#' + r).join(', ')}` : `Order #${alert.orderRef}`}
              </p>
              <p style={{ color: '#BFDBFE', fontSize: 13, margin: 0 }}>
                {alert.riderName} accepted and is heading to pick up
                {alert.riderPhone ? ` · 📞 ${alert.riderPhone}` : ''}
              </p>
            </div>
          </div>
          <button
            style={{ padding: '6px 14px', backgroundColor: 'rgba(255,255,255,0.2)', color: '#FFF', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}
            onClick={() => setRiderAlerts(prev => prev.filter((_, j) => j !== i))}
          >
            ✕
          </button>
        </div>
      ))}

      {/* NAVBAR */}
      <div style={{ ...s.navbar, marginTop: bannerOffset }}>
        <div style={s.navLeft}>
          <button
            style={{ padding:'8px 10px', backgroundColor:'rgba(255,255,255,0.15)', color:'#FFF', border:'1px solid rgba(255,255,255,0.3)', borderRadius:8, cursor:'pointer', fontSize:16, lineHeight:1 }}
            onClick={()=>setTabsOpen(v=>!v)}
            title={tabsOpen ? 'Hide menu' : 'Show menu'}
          >
            ☰
          </button>
          <div style={{ backgroundColor:'#FFF', borderRadius:8, padding:'6px 12px', display:'flex', alignItems:'center' }}>
            <img src={logo} alt="PharmDev" style={{ height:38 }} />
          </div>
          <span style={s.navTitle}>Dashboard</span>
        </div>
        <div style={s.navRight}>
          <span style={s.navUser}>{pharmacyData?.pharmacyName}</span>
          {counts.pending > 0 && (
            <span style={{ backgroundColor: '#EF4444', color: '#FFF', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>
              🔴 {counts.pending} pending
            </span>
          )}
          {unreadChats > 0 && (
            <span style={{ backgroundColor: '#3B82F6', color: '#FFF', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              onClick={() => setActiveTab('chat')}>
              💬 {unreadChats} new message{unreadChats > 1 ? 's' : ''}
            </span>
          )}
          <button
            style={{
              padding: '5px 14px',
              backgroundColor: !isOnline
                ? '#FEE2E2'
                : isOpen ? '#DCFCE7' : '#FEE2E2',
              color: !isOnline
                ? '#B91C1C'
                : isOpen ? '#15803D' : '#B91C1C',
              border: `1px solid ${!isOnline
                ? '#FCA5A5'
                : isOpen ? '#86EFAC' : '#FCA5A5'}`,
              borderRadius: 8,
              cursor: isOnline ? 'pointer' : 'not-allowed',
              fontSize: 13, fontWeight: 700,
            }}
            onClick={isOnline ? toggleOpen : undefined}
            title={!isOnline ? 'Reconnect to internet to change status' : ''}
          >
            {!isOnline ? '🔴 Offline — Closed' : isOpen ? '🟢 Open' : '🔴 Closed'}
          </button>
          {/* DARK MODE */}
          <button
            style={{ padding: '5px 12px', backgroundColor: 'rgba(255,255,255,0.1)', color: '#FFF', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, cursor: 'pointer', fontSize: 16 }}
            onClick={toggleDarkMode}
            title="Toggle dark mode"
          >
            {darkMode ? '☀️' : '🌙'}
          </button>
          <button style={s.guideBtn} onClick={() => setShowGuide(true)}>📖 Guide</button>
          <button style={s.logoutBtn} onClick={onLogout}>Log out</button>
        </div>
      </div>

      {/* OFFLINE BANNER */}
      {!isOnline && (
        <div style={{
          backgroundColor: '#EF4444', color: '#FFF',
          padding: '12px 24px', textAlign: 'center',
          fontWeight: '700', fontSize: 14, letterSpacing: 0.5,
          display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 20 }}>⚠️</span>
          No internet — your pharmacy has been automatically set to CLOSED on the customer app. Reconnect to reopen.
        </div>
      )}

      <div style={{ ...s.content, maxWidth: tabsOpen ? 1200 : '100%' }}>
        {/* TABS */}
        {tabsOpen && (
          <div style={s.dashTabs}>
            {tabs.map(t => (
              <button
                key={t.id}
                style={{ ...s.dashTab, ...(activeTab === t.id ? s.dashTabActive : {}) }}
                onClick={() => setActiveTab(t.id)}
              >
                {t.label}
                {t.id === 'chat' && unreadChats > 0 && (
                  <span style={{ marginLeft: 4, backgroundColor: '#EF4444', color: '#FFF', borderRadius: 20, padding: '0 6px', fontSize: 10, fontWeight: 700 }}>{unreadChats}</span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* ORDERS TAB */}
        {activeTab === 'orders' && (
          <>
            <div style={s.statsRow}>
              {[
                { label: 'Total',      value: counts.all,        color: '#1D9E75' },
                { label: 'Pending',    value: counts.pending,    color: '#F59E0B' },
                { label: 'Preparing',  value: counts.preparing,  color: '#3B82F6' },
                { label: 'Delivering', value: counts.delivering + counts.dispatched, color: '#F97316' },
                { label: 'Delivered',  value: counts.delivered,  color: '#10B981' },
              ].map((st, i) => (
                <div key={i} style={s.statCard}>
                  <p style={{ ...s.statValue, color: st.color }}>{st.value}</p>
                  <p style={s.statLabel}>{st.label}</p>
                </div>
              ))}
            </div>

            <div style={s.filterRow}>
              <button style={{ ...s.filterBtn, ...(filter === 'new' ? { backgroundColor: '#EF4444', color: '#FFF', borderColor: '#EF4444' } : {}) }} onClick={() => setFilter('new')}>
                🆕 New Orders <span style={s.filterCount}>{newOrders.length}</span>
              </button>
              {['all', ...STATUS_OPTIONS].map(status => (
                <button key={status} style={{ ...s.filterBtn, ...(filter === status ? s.filterBtnActive : {}) }} onClick={() => setFilter(status)}>
                  {status === 'all' ? '📋 All' : `${STATUS_ICONS[status]} ${status}`}
                  <span style={s.filterCount}>{status === 'all' ? counts.all : counts[status] || 0}</span>
                </button>
              ))}
            </div>

            {loading ? (
              <div style={s.loadingState}><p>Loading orders...</p></div>
            ) : filtered.length === 0 ? (
              <div style={s.emptyState}>
                <p style={{ fontSize: 40 }}>{filter === 'new' ? '✅' : '📭'}</p>
                <p style={{ color: '#888', fontWeight: 600 }}>{filter === 'new' ? 'No new orders right now' : 'No orders found'}</p>
                {filter === 'new' && <p style={{ color: '#AAA', fontSize: 13 }}>New orders will appear here with a ringtone alert</p>}
              </div>
            ) : (
              <div style={s.ordersGrid}>
                {filtered.map(order => (
                  <OrderCard key={order.id} order={order} updating={updating} onUpdate={updateStatus} isNew={newOrderIds.has(order.id)} pharmacyData={pharmacyData} pharmacyId={pharmacyId} />
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'prescriptions' && <PrescriptionTab pharmacyId={pharmacyId} pharmacyData={pharmacyData} />}
        {activeTab === 'chat'          && <ChatTab pharmacyId={pharmacyId} pharmacyData={pharmacyData} />}
        {activeTab === 'inventory'     && <InventoryTab pharmacyId={pharmacyId} pharmacyData={pharmacyData} />}
        {activeTab === 'sales'         && <SalesTab pharmacyId={pharmacyId} pharmacyData={pharmacyData} orders={orders} />}
        {activeTab === 'earnings'      && <EarningsTab pharmacyId={pharmacyId} pharmacyData={pharmacyData} orders={orders} />}
        {activeTab === 'hours'         && <WorkingHoursTab pharmacyId={pharmacyId} pharmacyData={pharmacyData} />}
        {activeTab === 'profile'       && <ProfileTab pharmacyId={pharmacyId} pharmacyData={pharmacyData} />}
        {activeTab === 'pharmacists'   && <PharmacistsTab pharmacyId={pharmacyId} />}
        {activeTab === 'bank_account' && <BankAccountTab pharmacyId={pharmacyId} pharmacyData={pharmacyData} />}
        {activeTab === 'announcements' && <AnnouncementsTab pharmacyId={pharmacyId} />}
      </div>
    </div>
  );
}

// ── ROOT APP ──────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser]                   = useState(null);
  const [checking, setChecking]           = useState(true);
  const [showRegister, setShowRegister]   = useState(false);
  const [pharmacyData, setPharmacyData]   = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, u => {
      setUser(u);
      if (u) {
        onValue(ref(db, `pharmacies/${u.uid}`), snap => {
          if (snap.exists()) setPharmacyData(snap.val());
          setChecking(false);
        });
      } else { setChecking(false); setPharmacyData(null); }
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    setUser(null);
    setPharmacyData(null);
  };

  if (checking) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <p style={{ color: '#888' }}>Loading...</p>
    </div>
  );

  if (!user) return showRegister
    ? <RegisterPage onSwitch={() => setShowRegister(false)} />
    : <LoginPage onSwitch={() => setShowRegister(true)} />;

  if (pharmacyData?.status === 'pending') return <PendingPage onLogout={handleLogout} />;

  return <Dashboard onLogout={handleLogout} pharmacyId={user.uid} pharmacyData={pharmacyData} />;
}
